import { Link } from "react-router-dom";
import {
  Package,
  Calendar,
  ChartBar,
  Buildings,
  UserPlus,
  UploadSimple,
  ChartLineUp,
  ArrowRight,
  EnvelopeSimple,
  CheckCircle,
  ShieldCheck,
  CurrencyCircleDollar,
  Receipt,
  Wallet,
  CalendarCheck,
  Warning,
  ChartPieSlice,
} from "@phosphor-icons/react";

/* ─────────────────────── Data ─────────────────────── */

const FEATURES = [
  {
    icon: Package,
    title: "Suivi en temps réel",
    desc: "Stock, mouvements, alertes de seuil. Tout en un coup d'œil.",
    color: "bg-indigo-50 text-indigo-600",
  },
  {
    icon: Calendar,
    title: "Gestion des péremptions",
    desc: "Alertes automatiques, prévisions, zéro gaspillage.",
    color: "bg-amber-50 text-amber-600",
  },
  {
    icon: CurrencyCircleDollar,
    title: "Finances fournisseurs",
    desc: "Factures, échéanciers, rappels de paiement. Votre trésorerie sous contrôle.",
    color: "bg-emerald-50 text-emerald-600",
  },
  {
    icon: ChartBar,
    title: "Tableaux de bord",
    desc: "KPIs, tendances, analyses financières pour piloter votre activité.",
    color: "bg-cyan-50 text-cyan-600",
  },
  {
    icon: Buildings,
    title: "Multi-sites",
    desc: "Un espace par structure. Chacun avec son thème, ses utilisateurs, ses données.",
    color: "bg-purple-50 text-purple-600",
  },
  {
    icon: ShieldCheck,
    title: "Sécurité & conformité",
    desc: "Rôles utilisateurs, audit trail, RGPD. Vos données sont protégées.",
    color: "bg-rose-50 text-rose-600",
  },
];

const STEPS = [
  {
    num: "1",
    icon: UserPlus,
    title: "Créez votre espace en 2 minutes",
    desc: "Nom, email, mot de passe — c'est tout.",
  },
  {
    num: "2",
    icon: UploadSimple,
    title: "Importez vos produits",
    desc: "Fichier Excel ou saisie manuelle, à vous de choisir.",
  },
  {
    num: "3",
    icon: ChartLineUp,
    title: "Pilotez vos stocks au quotidien",
    desc: "Dashboard, alertes, commandes — tout est automatisé.",
  },
];

/* ─────────────────── Components ───────────────────── */

function NavBar() {
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-gray-100">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <span className="flex items-center gap-2">
          <img src="/logos/quantelys-icon-64.png" alt="" className="h-8 w-8" />
          <span className="text-xl font-extrabold tracking-tight text-indigo-600">
            Quantelys
          </span>
        </span>
        <div className="flex items-center gap-3">
          <Link
            to="/login"
            className="text-sm font-medium text-gray-600 hover:text-gray-900 transition hidden sm:block"
          >
            Se connecter
          </Link>
          <Link
            to="/register"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition shadow-sm"
          >
            Essai gratuit
            <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </div>
    </nav>
  );
}

function Hero() {
  return (
    <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 overflow-hidden">
      {/* Background blobs */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] pointer-events-none">
        <div className="absolute top-10 left-10 w-72 h-72 bg-indigo-200/40 rounded-full blur-3xl animate-pulse" />
        <div className="absolute top-32 right-10 w-64 h-64 bg-purple-200/30 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
        <div className="absolute bottom-0 left-1/3 w-56 h-56 bg-sky-200/25 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "2s" }} />
      </div>

      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 text-center">
        {/* Logo */}
        <img src="/logos/quantelys-icon-192.png" alt="Quantelys" className="h-20 w-20 mx-auto mb-6" />

        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700 text-xs font-semibold mb-6">
          <ShieldCheck size={14} weight="fill" />
          Essai gratuit 7 jours — Sans carte bancaire
        </div>

        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-gray-900 leading-tight">
          La gestion de stock{" "}
          <span className="text-indigo-600">intelligente</span> pour les
          professionnels de santé
        </h1>

        <p className="mt-6 text-lg sm:text-xl text-gray-500 max-w-2xl mx-auto leading-relaxed">
          Suivez vos stocks, anticipez les ruptures, optimisez vos commandes.
          Simple, rapide, sécurisé.
        </p>

        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl bg-indigo-600 text-white text-base font-bold hover:bg-indigo-700 transition shadow-lg shadow-indigo-200 hover:shadow-indigo-300"
          >
            Essai gratuit — 7 jours
            <ArrowRight size={20} weight="bold" />
          </Link>
          <Link
            to="/login"
            className="text-sm font-medium text-gray-500 hover:text-indigo-600 transition"
          >
            Déjà un compte ? Se connecter →
          </Link>
        </div>
      </div>
    </section>
  );
}

function Features() {
  return (
    <section id="features" className="py-20 sm:py-28 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-14">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
            Tout ce qu'il faut pour gérer vos stocks
          </h2>
          <p className="mt-4 text-gray-500 text-lg max-w-2xl mx-auto">
            Conçu pour les pharmacies, cabinets dentaires, cliniques et
            laboratoires.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map((f) => (
            <div
              key={f.title}
              className="group relative rounded-2xl border border-gray-100 bg-white p-6 hover:shadow-lg hover:border-indigo-100 transition-all duration-300"
            >
              <div
                className={`inline-flex items-center justify-center w-12 h-12 rounded-xl ${f.color} mb-4`}
              >
                <f.icon size={24} weight="duotone" />
              </div>
              <h3 className="text-base font-bold text-gray-900 mb-2">
                {f.title}
              </h3>
              <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinanceHighlight() {
  const FINANCE_FEATURES = [
    {
      icon: Receipt,
      title: "Factures fournisseurs",
      desc: "Enregistrez vos factures, joignez les justificatifs. Tout est centralisé.",
    },
    {
      icon: CalendarCheck,
      title: "Échéancier de paiement",
      desc: "Paiement unique ou échelonné — suivez chaque échéance avec précision.",
    },
    {
      icon: Warning,
      title: "Alertes retard",
      desc: "Repérez instantanément les factures en retard et les montants en souffrance.",
    },
    {
      icon: ChartPieSlice,
      title: "Dashboard financier",
      desc: "À payer, en retard, payé ce mois, à venir — vos KPIs en un coup d’œil.",
    },
    {
      icon: Wallet,
      title: "Barre de progression",
      desc: "Visualisez l’avancement du règlement de chaque facture.",
    },
    {
      icon: CurrencyCircleDollar,
      title: "Prévisions de trésorerie",
      desc: "Anticipez vos sorties sur 7 jours pour mieux gérer votre cash-flow.",
    },
  ];

  return (
    <section className="py-20 sm:py-28 bg-gradient-to-br from-emerald-50/60 via-white to-indigo-50/40">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-700 text-xs font-semibold mb-4">
            <CurrencyCircleDollar size={14} weight="fill" />
            Nouveau module
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
            Pilotez vos finances fournisseurs
          </h2>
          <p className="mt-4 text-gray-500 text-lg max-w-2xl mx-auto">
            Plus qu’un gestionnaire de stocks — Quantelys vous aide à maîtriser
            vos paiements, vos échéances et votre trésorerie.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {FINANCE_FEATURES.map((f) => (
            <div
              key={f.title}
              className="group relative rounded-2xl border border-emerald-100 bg-white p-6 hover:shadow-lg hover:border-emerald-200 transition-all duration-300"
            >
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 mb-4">
                <f.icon size={24} weight="duotone" />
              </div>
              <h3 className="text-base font-bold text-gray-900 mb-2">
                {f.title}
              </h3>
              <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 transition shadow-lg shadow-emerald-200"
          >
            Essayer le module Finances
            <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="py-20 sm:py-28 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-14">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
            Opérationnel en 3 étapes
          </h2>
          <p className="mt-4 text-gray-500 text-lg">
            Pas de formation, pas d'installation. Juste un navigateur.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {STEPS.map((s) => (
            <div key={s.num} className="relative text-center">
              {/* Step number */}
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-indigo-600 text-white text-xl font-extrabold mb-5 shadow-lg shadow-indigo-200">
                {s.num}
              </div>
              <h3 className="text-base font-bold text-gray-900 mb-2">
                {s.title}
              </h3>
              <p className="text-sm text-gray-500 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  return (
    <section id="pricing" className="py-20 sm:py-28 bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
        <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
          Un tarif simple et transparent
        </h2>
        <p className="mt-4 text-gray-500 text-lg mb-12">
          Commencez gratuitement, payez quand vous êtes convaincu.
        </p>

        <div className="relative rounded-2xl border-2 border-indigo-200 bg-white p-8 sm:p-10 shadow-sm max-w-md mx-auto">
          {/* Badge */}
          <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-indigo-600 text-white text-xs font-bold">
            Offre de lancement
          </div>

          <div className="mt-2">
            <div className="text-sm font-semibold text-indigo-600 mb-1">
              Essai gratuit 7 jours
            </div>
            <div className="flex items-baseline justify-center gap-1">
              <span className="text-5xl font-extrabold text-gray-900">
                750€
              </span>
              <span className="text-gray-400 text-lg">/an HT</span>
            </div>
            <p className="text-sm text-gray-400 mt-1">
              Soit 62,50€/mois · Paiement annuel
            </p>
          </div>

          <div className="mt-8 space-y-3 text-left">
            {[
              "Produits, mouvements, fournisseurs illimités",
              "Alertes de seuil et péremptions",
              "Tableaux de bord et analyses financières",
              "Gestion des commandes + export PDF",
              "Import/export Excel",
              "Jusqu'à 5 utilisateurs inclus",
              "Sous-domaine personnalisé",
              "Module finances & échéancier fournisseurs",
              "Support par email + chat IA (Hugo)",
            ].map((item) => (
              <div key={item} className="flex items-start gap-2.5">
                <CheckCircle
                  size={18}
                  weight="fill"
                  className="text-indigo-500 mt-0.5 flex-shrink-0"
                />
                <span className="text-sm text-gray-700">{item}</span>
              </div>
            ))}
          </div>

          <Link
            to="/register"
            className="mt-8 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-indigo-600 text-white text-base font-bold hover:bg-indigo-700 transition shadow-lg shadow-indigo-200"
          >
            Commencer gratuitement
            <ArrowRight size={18} weight="bold" />
          </Link>

          <p className="mt-4 text-xs text-gray-400">
            Sans engagement pendant l'essai · Aucune carte bancaire requise · Paiement sécurisé par Stripe
          </p>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-gray-100 bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-2">
              <img src="/logos/quantelys-icon-64.png" alt="" className="h-7 w-7" />
              <span className="text-base font-extrabold text-indigo-600 tracking-tight">
                Quantelys
              </span>
            </span>
            <span className="text-xs text-gray-400">© 2026</span>
          </div>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-500">
            <a
              href="mailto:contact@quantelys.com"
              className="hover:text-indigo-600 transition inline-flex items-center gap-1.5"
            >
              <EnvelopeSimple size={14} />
              contact@quantelys.com
            </a>
            <Link to="/login" className="hover:text-indigo-600 transition">
              Se connecter
            </Link>
            <Link to="/register" className="hover:text-indigo-600 transition">
              Créer un compte
            </Link>
            <Link to="/privacy" className="hover:text-indigo-600 transition">
              Confidentialité
            </Link>
            <Link to="/terms" className="hover:text-indigo-600 transition">
              CGU
            </Link>
            <Link to="/legal" className="hover:text-indigo-600 transition">
              Mentions légales
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ─────────────────── Main Page ────────────────────── */

export default function Landing() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50/50 via-white to-white">
      <NavBar />
      <Hero />
      <Features />
      <FinanceHighlight />
      <HowItWorks />
      <Pricing />
      <Footer />
    </div>
  );
}
